import { AxiosError } from 'axios'
import { Button, Notification } from 'bloomer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { IAlphaprocessState } from '../store'

interface CustomMessage {
  status: number
  message: string
}

interface Props {
  error: AxiosError
  customErrorMessages?: CustomMessage[]
  // shows a "try again" button for connection problems
  onRetry?: () => void
}

// a connection problem with a retry: tried again by itself this often, and as soon as the app is back online
const AUTO_RETRY_MS = 15000

// Status codes of the proxy while the backend is restarting or unreachable
const UNAVAILABLE_STATUS_CODES = [502, 503, 504]

// Browser messages for failed requests: Safari "Load failed", Chrome "Failed to fetch",
// Firefox "NetworkError when attempting to fetch resource.", axios "Network Error"/timeouts
const CONNECTION_ERROR_PATTERN = /load failed|failed to fetch|networkerror|network error|timeout/i

export function isConnectionError(error: any): boolean {
  if (!error) {
    return false
  }
  if (error.response) {
    return UNAVAILABLE_STATUS_CODES.includes(error.response.status)
  }
  return error.code === 'ECONNABORTED' || CONNECTION_ERROR_PATTERN.test(error.message ?? '')
}

/**
 *  This component renders information about axios errors
 *
 * @param  props
 */
export default function ErrorView({ error, customErrorMessages = [], onRetry }: Props) {
  const { t } = useTranslation()
  const isOffline = useSelector((state: IAlphaprocessState) => state.core.isOffline)
  const [showDetails, setShowDetails] = useState(false)

  // connection problems heal by themselves: retry when the app is back online, and now and then, so a box
  // "not reachable" does not stay up after the connection returned
  const autoRetry = !!onRetry && isConnectionError(error)
  const retryRef = useRef(onRetry)
  retryRef.current = onRetry
  const wasOffline = useRef(isOffline)
  useEffect(() => {
    if (autoRetry && wasOffline.current && !isOffline) retryRef.current?.()
    wasOffline.current = isOffline
  }, [autoRetry, isOffline])
  useEffect(() => {
    if (!autoRetry) return undefined
    const retry = () => { if (document.visibilityState === 'visible') retryRef.current?.() }
    const timer = window.setInterval(retry, AUTO_RETRY_MS)
    window.addEventListener('online', retry)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('online', retry)
    }
  }, [autoRetry])

  const toggleDetails = useCallback(() => {
    setShowDetails(!showDetails)
  }, [showDetails])

  let message: any = t('Error-System')

  // eslint-disable-next-line no-console
  console.error('error', error)

  const data = error?.response?.data as any

  const connectionProblem = (
    <Notification isColor="warning" className="has-line-breaks error-view">
      { t('ConnectBackendFailed') }
      { onRetry && (
        <div className="has-margin-top-10">
          <Button isSize="small" onClick={onRetry}>{ t('errorPages.tryAgain') }</Button>
        </div>
      ) }
    </Notification>
  )

  if (isConnectionError(error)) {
    return isOffline ? null : connectionProblem
  }

  if (error?.response?.status === 429) {
    // rate limit (e.g. too many processes created via a shared link)
    message = t('errorPages.tooManyRequests')
  } else if (error.name === 'DatabaseClosedError') {
    message = t('errorPages.databaseClosed')
  } else if (data?.translation_key?.length) {
    const placeholders = data?.placeholders
    message = t(data.translation_key, placeholders)
  } else if (error && error.name === 'ValidationError') {
    message = error.message
  } else if (error && error.response && typeof error.response.data === 'string') {
    // in some cases we get an empty response.
    // then show the default error message
    if (data.length) {
      message = error.response.data
    } else if (error.response.status === 401) {
      // If we get an 401 error with an empty response then show generic error message
      // for missing permissions
      message = t('errorPages.unauthorizedAction')
    }
  } else if (error.response?.status === 404) {
    // Check if this is a deleted checklist error
    if (data?.checklist_discarded) {
      message = t('Error-DeletedChecklist')
    } else {
      const cm = customErrorMessages.find((m) => m.status === 404)
      message = cm?.message ?? t('Error-Resource')
    }
  } else if (error && error.response && data.message) {
    message = data.message
  } else if (error && error.response && data.error) {
    message = data.error
  } else if (error && error.response && error.response.status) {
    if (error.response.status === 404) {
      message = t('Error-Resource')
    } else if (error.response && error.response.status === 403
      && data.license_expired) {
      // force logout if the license expired
      message = data.license_expired
      // doLogout()
      // history.push('/login')
    }
  } else {
    // no response from the server
    return isOffline ? null : connectionProblem
  }

  const details = showDetails ? (
    <div>
      Status Code:
      { error.response?.status }
      <br />
      Message:
      { error.message }
      <br />
      Body:
      { error.response?.data ? JSON.stringify(error.response.data) : 'No response data' }
      <br />
      URL:
      { error.request?.responseURL }
      <br />
    </div>
  ) : null

  return (
    <Notification isColor="danger" className="has-line-breaks error-view">
      { message }
      <div className="error-details-toggle">
        { showDetails
          ? <i onClick={toggleDetails} className="fas fa-chevron-up" />
          : <i onClick={toggleDetails} className="fas fa-chevron-down" />}
      </div>
      { details }
    </Notification>
  )
}
